import { JobRow } from './JobRow';
import type { Job } from '@/lib/api/jobs';

interface JobsTableProps {
  jobs: Job[];
  loading: boolean;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string) => void;
  onEdit?: (job: Job) => void;
  onEditSchedule?: (job: Job) => void;
  onDuplicate?: (job: Job) => void;
  onDelete?: (job: Job) => void;
  selectedIds?: Set<number>;
  onSelectionChange?: (ids: Set<number>) => void;
}

export const JobsTable = ({
  jobs,
  loading,
  sortColumn,
  sortDirection,
  onSort,
  onEdit,
  onEditSchedule,
  onDuplicate,
  onDelete,
  selectedIds,
  onSelectionChange,
}: JobsTableProps) => {
  const allSelected = jobs.length > 0 && jobs.every(j => selectedIds?.has(j.id));
  const someSelected = jobs.some(j => selectedIds?.has(j.id));

  const toggleAll = () => {
    if (!onSelectionChange) return;
    if (allSelected) {
      const next = new Set(selectedIds);
      jobs.forEach(j => next.delete(j.id));
      onSelectionChange(next);
    } else {
      const next = new Set(selectedIds);
      jobs.forEach(j => next.add(j.id));
      onSelectionChange(next);
    }
  };

  const toggleOne = (id: number) => {
    if (!onSelectionChange || !selectedIds) return;
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    onSelectionChange(next);
  };
  const handleHeaderClick = (column: string) => {
    if (onSort) {
      onSort(column);
    }
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <span className="material-symbols-outlined text-base opacity-30">unfold_more</span>;
    }
    return sortDirection === 'asc' ? (
      <span className="material-symbols-outlined text-base text-primary">arrow_upward</span>
    ) : (
      <span className="material-symbols-outlined text-base text-primary">arrow_downward</span>
    );
  };

  return (
    <div className="rounded-xl bg-white border border-gray-200 min-h-[100px]">
      <div className="overflow-x-auto overflow-y-visible">
        <table className="w-full relative">
        <thead className="border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
          <tr>
            {onSelectionChange && (
              <th className="pl-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={toggleAll}
                  className="rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                  title="Select all"
                />
              </th>
            )}
            <th
              className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-primary/5"
              onClick={() => handleHeaderClick('jobNumber')}
            >
              <div className="flex items-center gap-2">
                Job #
                {getSortIcon('jobNumber')}
              </div>
            </th>
            <th
              className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-primary/5"
              onClick={() => handleHeaderClick('title')}
            >
              <div className="flex items-center gap-2">
                Title
                {getSortIcon('title')}
              </div>
            </th>
            <th
              className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-primary/5"
              onClick={() => handleHeaderClick('client')}
            >
              <div className="flex items-center gap-2">
                Client
                {getSortIcon('client')}
              </div>
            </th>
            <th
              className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-primary/5"
              onClick={() => handleHeaderClick('status')}
            >
              <div className="flex items-center gap-2">
                Status
                {getSortIcon('status')}
              </div>
            </th>
            <th
              className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-primary/5"
              onClick={() => handleHeaderClick('crew')}
            >
              <div className="flex items-center gap-2">
                Crew
                {getSortIcon('crew')}
              </div>
            </th>
            <th
              className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-primary/5"
              onClick={() => handleHeaderClick('startDate')}
            >
              <div className="flex items-center gap-2">
                Start Date
                {getSortIcon('startDate')}
              </div>
            </th>
            <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-light">
          {loading ? (
            <>
              {[1, 2, 3, 4, 5].map(i => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="px-4 py-3"><div className="h-3.5 bg-gray-100 rounded animate-pulse w-16" /></td>
                  <td className="px-4 py-3"><div className="h-3.5 bg-gray-100 rounded animate-pulse w-36" /></td>
                  <td className="px-4 py-3 hidden sm:table-cell"><div className="h-3.5 bg-gray-100 rounded animate-pulse w-28" /></td>
                  <td className="px-4 py-3"><div className="h-5 bg-gray-100 rounded-full animate-pulse w-20" /></td>
                  <td className="px-4 py-3 hidden lg:table-cell"><div className="h-3.5 bg-gray-100 rounded animate-pulse w-20" /></td>
                  <td className="px-4 py-3 hidden md:table-cell"><div className="h-3.5 bg-gray-100 rounded animate-pulse w-16" /></td>
                  <td className="px-4 py-3"><div className="h-6 bg-gray-100 rounded animate-pulse w-16 ml-auto" /></td>
                </tr>
              ))}
            </>
          ) : jobs.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-4 py-16 text-center">
                <span className="material-symbols-outlined text-gray-300 text-5xl block mx-auto mb-3">work_off</span>
                <p className="text-gray-500 text-sm font-medium">No jobs found</p>
                <p className="text-gray-400 text-xs mt-1">Try adjusting your filters or search terms</p>
              </td>
            </tr>
          ) : (
            jobs.map((job) => (
              <JobRow
                key={job.id}
                job={job}
                onEdit={onEdit}
                onEditSchedule={onEditSchedule}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
                isSelected={selectedIds?.has(job.id)}
                onToggleSelect={onSelectionChange ? () => toggleOne(job.id) : undefined}
              />
            ))
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
};


